/**
 * useEmailRecipientDndData
 *
 * DASH-EMAIL-010: real-data hook for the Email Deliverability view's
 * "Email DND" presentation -- an independent fetch against the real,
 * read-only RECIPIENT-grain DND Dashboard API
 * (emailDeliveryRecipientDndApi.js -> GET /api/email-delivery/dashboard/
 * recipient-dnd), mirroring useEmailHygieneReconciliationActiveCount.js's
 * own independent-fetch pattern: this is a strictly separate state machine
 * from useEmailDeliverabilityData.js's hygiene grain, so a failure here
 * must never block the rest of the (hygiene) view from rendering, and a
 * hygiene fetch failure must never affect this hook's own result either.
 *
 * Never fabricates a DND number. On:
 *   - a request failure (network/5xx/etc),
 *   - a malformed/unrecognized response shape (evidence_status is neither
 *     "available" nor "unavailable"),
 *   - or the backend's own evidence_status === "unavailable"
 *       (no GHL workflow has ever reported an Email DND Required value for
 *        this tenant -- NOT "0 DND recipients"),
 * every count is surfaced as `null`. A real, backend-confirmed zero for
 * one bucket (evidence_status === "available") is preserved as an honest
 * 0, never coerced to null and never confused with the unavailable case.
 */

import { useState, useEffect, useCallback } from "react";
import { getEmailDeliveryRecipientDnd } from "../lib/emailDeliveryRecipientDndApi";

function numberOrNull(value) {
  return typeof value === "number" ? value : null;
}

export function useEmailRecipientDndData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dndFlaggedCount, setDndFlaggedCount] = useState(null);
  const [dndClearedCount, setDndClearedCount] = useState(null);
  const [syncNeedsAttentionCount, setSyncNeedsAttentionCount] = useState(null);
  const [evidenceStatus, setEvidenceStatus] = useState(null);

  const fetchDnd = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getEmailDeliveryRecipientDnd();
      const status = response?.evidence_status;

      if (status !== "available" && status !== "unavailable") {
        // Malformed/unrecognized response shape -- never fabricate a DND
        // number from a shape this contract doesn't recognize.
        setEvidenceStatus("unavailable");
        setDndFlaggedCount(null);
        setDndClearedCount(null);
        setSyncNeedsAttentionCount(null);
        return;
      }

      setEvidenceStatus(status);
      if (status === "unavailable") {
        setDndFlaggedCount(null);
        setDndClearedCount(null);
        setSyncNeedsAttentionCount(null);
      } else {
        setDndFlaggedCount(numberOrNull(response.dnd_flagged_count));
        setDndClearedCount(numberOrNull(response.dnd_cleared_count));
        setSyncNeedsAttentionCount(numberOrNull(response.sync_needs_attention_count));
      }
    } catch (err) {
      // Defensive, independent of the hygiene fetch -- never blocks the
      // rest of the Deliverability view from rendering, never fabricates a
      // DND number on failure.
      setError(err?.message || "Failed to load Email DND data");
      setEvidenceStatus(null);
      setDndFlaggedCount(null);
      setDndClearedCount(null);
      setSyncNeedsAttentionCount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDnd();
  }, [fetchDnd]);

  return {
    loading,
    error,
    dndFlaggedCount,
    dndClearedCount,
    syncNeedsAttentionCount,
    evidenceStatus,
    refresh: fetchDnd,
  };
}

export default useEmailRecipientDndData;
